import { create } from "zustand";

/**
 * Shared Zustand store driving the workshop dashboard's modal stack.
 *
 * Why a store: the `/workshop/:id` page has two surfaces that need to
 * open the same modals — the `ModalTriggers` action row and the
 * `BriefPanel` list rows. In the vanilla source (`_legacy/wd.html`
 * lines 80-110, 336-374) both surfaces called the same global
 * `openAgendaModal()` / `openDocModal()` / `openQuestionnaireModal()`
 * functions. A small singleton store reproduces that shared-state
 * model without prop-drilling open/close handlers through the
 * dashboard.
 *
 * Only one modal is open at a time, so `active` is a single key
 * (or `null`). Same pattern as `useBubbleTooltip` in
 * `src/components/diagrams/useBubbleTooltip.ts`.
 */

export type OpenModal =
  | "agenda"
  | "map"
  | "bring"
  | "questionnaire"
  | "doc"
  | "data-tables"
  | "transcript"
  | "analysis"
  | "report"
  | "playback"
  | "captures"
  | "handshake"
  | "stress-test"
  | "meet-crew"
  | "haw"
  | "decision-log"
  // P0.3 — Track B's commit-history surface (RT-VERSIONING). Declared in
  // the Phase-0 contract; mounted by ModalTriggers + launched from HudHeader.
  | "version-history"
  // P0.3 — Track A's cross-cutting knowledge surfaces. Declared in the
  // Phase-0 contract; mounted by ModalTriggers + launched from HudHeader.
  // `insight-library` = MC-level curated rollup (INSIGHT-LIB);
  // `cross-search` = cross-workshop faceted search (CC-SEARCH);
  // `traceability` = cross-workshop link graph (CC-LINKS stage 2).
  | "insight-library"
  | "cross-search"
  | "traceability"
  // Facilitation-side questionnaire results (charts + user breakdown),
  // launched from the Asset Locker. Distinct from "questionnaire" — that is
  // the participant intake modal on the Brief panel.
  | "questionnaire-results"
  | null;

type WorkshopModalState = {
  active: OpenModal;
  open: (modal: Exclude<OpenModal, null>) => void;
  close: () => void;
};

export const useWorkshopModals = create<WorkshopModalState>((set) => ({
  active: null,
  open: (modal) => set({ active: modal }),
  close: () => set({ active: null }),
}));
